import { useRef, useState, useCallback } from "react";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";
import { AUDIO_CHUNK_SIZE_MB } from "@arna-med/shared";
import { createSequentialQueue } from "@/lib/sequential-queue";
import { withNetworkRetry } from "@/lib/retry";

interface UseSpeechRecognitionOptions {
  /**
   * `clientRequestId` is stable per chunk across retries — pass it on when
   * persisting the text so a replayed save cannot duplicate the transcript.
   */
  onTranscript: (text: string, isFinal: boolean, clientRequestId?: string) => void;
  chunkSizeMB?: number;
  onRecordingComplete?: (blob: Blob, mimeType: string, durationSeconds: number) => void;
}

interface PendingChunk {
  id: string;
  blob: Blob;
  mimeType: string;
}

interface UseSpeechRecognitionReturn {
  isRecording: boolean;
  isPaused: boolean;
  isSupported: boolean;
  start: () => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
}

interface UseSpeechRecognitionRecordReturn extends UseSpeechRecognitionReturn {
  isProcessing: boolean;
  pendingChunks: number;
  transcriptionError: boolean;
  /** >0 while a chunk is waiting out a backoff before its next attempt. */
  retryingChunks: number;
  /** Chunks that exhausted every attempt and are still held in memory. */
  failedChunks: number;
  retryFailedChunks: () => void;
  streamRef: ReturnType<typeof useRef<MediaStream | null>>;
}

export function useSpeechRecognition({
  onTranscript,
  chunkSizeMB = AUDIO_CHUNK_SIZE_MB,
  onRecordingComplete,
}: UseSpeechRecognitionOptions): UseSpeechRecognitionRecordReturn {
  const backend = useBackendAuthenticated();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingChunks, setPendingChunks] = useState(0);
  const [transcriptionError, setTranscriptionError] = useState(false);
  const [retryingChunks, setRetryingChunks] = useState(0);
  const [failedChunks, setFailedChunks] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const archiveMrRef = useRef<MediaRecorder | null>(null);
  const archiveChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const isRecordingActiveRef = useRef(false);
  const recordingStartRef = useRef<number>(0);
  const pausedDurationRef = useRef<number>(0);
  const pauseStartRef = useRef<number>(0);
  const pendingCountRef = useRef(0);
  const retryingCountRef = useRef(0);
  // Chunks that ran out of attempts. Kept in memory so the doctor can send them
  // once the connection is back instead of losing that part of the consultation.
  const failedChunksRef = useRef<PendingChunk[]>([]);
  // Sequential queue: chunks are transcribed one at a time to preserve order
  const chunkQueueRef = useRef<ReturnType<typeof createSequentialQueue<PendingChunk>> | null>(null);

  const isSupported =
    typeof window !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined";

  const chunkSizeBytes = chunkSizeMB * 1024 * 1024;

  const enqueueChunk = useCallback(
    (chunk: PendingChunk) => {
      pendingCountRef.current++;
      setPendingChunks(pendingCountRef.current);
      setIsProcessing(true);

      if (!chunkQueueRef.current) {
        chunkQueueRef.current = createSequentialQueue<PendingChunk>(async (item) => {
          try {
            // A dropped connection must not cost the doctor this part of the
            // conversation, so each chunk is retried with backoff before it is
            // considered failed — and even then it is held for a manual retry.
            const json = await withNetworkRetry(
              () => {
                const ext = item.mimeType.split("/")[1]?.split(";")[0] ?? "webm";
                const formData = new FormData();
                formData.append("audio", item.blob, `chunk.${ext}`);
                return backend<{ success: boolean; data: { text: string } }>({
                  method: "POST",
                  url: "/api/v1/transcribe",
                  data: formData,
                });
              },
              {
                onRetry: (attempt, delayMs, err) => {
                  console.warn(
                    `Chunk transcription attempt ${attempt} failed, retrying in ${delayMs}ms`,
                    err,
                  );
                  retryingCountRef.current++;
                  setRetryingChunks(retryingCountRef.current);
                },
              },
            );
            const text = json.data.text?.trim();
            if (text) onTranscript(text, true, item.id);
          } catch (err) {
            console.error("Chunk transcription failed:", err);
            failedChunksRef.current.push(item);
            setFailedChunks(failedChunksRef.current.length);
            setTranscriptionError(true);
          } finally {
            if (retryingCountRef.current > 0) {
              retryingCountRef.current = 0;
              setRetryingChunks(0);
            }
            pendingCountRef.current--;
            setPendingChunks(pendingCountRef.current);
            if (pendingCountRef.current === 0) setIsProcessing(false);
          }
        });
      }

      chunkQueueRef.current.push(chunk);
      void chunkQueueRef.current.drain();
    },
    [backend, onTranscript],
  );

  const transcribeAndNotify = useCallback(
    (blob: Blob, mimeType: string) => {
      enqueueChunk({ id: crypto.randomUUID(), blob, mimeType });
    },
    [enqueueChunk],
  );

  const retryFailedChunks = useCallback(() => {
    const chunks = failedChunksRef.current;
    if (chunks.length === 0) return;
    failedChunksRef.current = [];
    setFailedChunks(0);
    setTranscriptionError(false);
    // Same chunk id as the first attempt: if one of those earlier attempts did
    // reach the server, the replay is deduplicated instead of doubling the text.
    chunks.forEach(enqueueChunk);
  }, [enqueueChunk]);

  const startChunkedRecorder = useCallback(
    (stream: MediaStream) => {
      const chunks: Blob[] = [];
      let accumulatedSize = 0;
      let rollingOver = false;

      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data.size === 0) return;
        chunks.push(e.data);
        accumulatedSize += e.data.size;
        if (accumulatedSize >= chunkSizeBytes && !rollingOver) {
          rollingOver = true;
          mr.stop();
        }
      };

      mr.onstop = () => {
        const mimeType = mr.mimeType || "audio/webm";
        const blob = new Blob(chunks, { type: mimeType });

        if (rollingOver && isRecordingActiveRef.current) {
          // Size-based rollover: restart recorder immediately, then process chunk
          startChunkedRecorder(stream);
          void transcribeAndNotify(blob, mimeType);
        } else {
          // Final stop (user stopped or rollover + user stopped between)
          const durationSeconds = recordingStartRef.current
            ? (Date.now() - recordingStartRef.current) / 1000
            : 0;
          if (blob.size > 0 && durationSeconds >= 10) {
            void transcribeAndNotify(blob, mimeType);
          }
          // Safety cleanup if stop() didn't already clear these
          streamRef.current?.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
          setIsRecording(false);
        }
      };

      mr.start(250); // emit data every 250ms for responsive size checking
    },
    [chunkSizeBytes, transcribeAndNotify],
  );

  const start = useCallback(async () => {
    if (isRecordingActiveRef.current) return;
    setTranscriptionError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      archiveChunksRef.current = [];

      // Archive recorder: runs the full session uninterrupted, produces one merged blob
      const archiveMr = new MediaRecorder(stream);
      archiveMrRef.current = archiveMr;
      archiveMr.ondataavailable = (e) => {
        if (e.data.size > 0) archiveChunksRef.current.push(e.data);
      };
      archiveMr.onstop = () => {
        const mimeType = archiveMr.mimeType || "audio/webm";
        const blob = new Blob(archiveChunksRef.current, { type: mimeType });
        const durationSeconds = recordingStartRef.current
          ? (Date.now() - recordingStartRef.current - pausedDurationRef.current) / 1000
          : 0;
        if (blob.size > 0) onRecordingComplete?.(blob, mimeType, durationSeconds);
      };
      archiveMr.start();

      isRecordingActiveRef.current = true;
      recordingStartRef.current = Date.now();
      pausedDurationRef.current = 0;
      pauseStartRef.current = 0;
      setIsRecording(true);
      setIsPaused(false);
      startChunkedRecorder(stream);
    } catch (err) {
      console.error("Failed to start recording:", err);
    }
  }, [startChunkedRecorder, onRecordingComplete]);

  const stop = useCallback(() => {
    if (!isRecordingActiveRef.current) return;
    isRecordingActiveRef.current = false;

    // If paused, accumulate the final paused duration before stopping
    if (pauseStartRef.current > 0) {
      pausedDurationRef.current += Date.now() - pauseStartRef.current;
      pauseStartRef.current = 0;
    }

    setIsPaused(false);

    // Stop stream tracks immediately (turns off mic indicator; both recorders
    // will flush remaining data and fire onstop)
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    const archiveMr = archiveMrRef.current;
    if (archiveMr && archiveMr.state !== "inactive") archiveMr.stop();

    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
  }, []);

  const pause = useCallback(() => {
    if (!isRecordingActiveRef.current || pauseStartRef.current > 0) return;
    pauseStartRef.current = Date.now();
    setIsPaused(true);

    const archiveMr = archiveMrRef.current;
    if (archiveMr && archiveMr.state === "recording") archiveMr.pause();

    const mr = mediaRecorderRef.current;
    if (mr && mr.state === "recording") mr.pause();
  }, []);

  const resume = useCallback(() => {
    if (!isRecordingActiveRef.current || pauseStartRef.current === 0) return;
    pausedDurationRef.current += Date.now() - pauseStartRef.current;
    pauseStartRef.current = 0;
    setIsPaused(false);

    const archiveMr = archiveMrRef.current;
    if (archiveMr && archiveMr.state === "paused") archiveMr.resume();

    const mr = mediaRecorderRef.current;
    if (mr && mr.state === "paused") mr.resume();
  }, []);

  return { isRecording, isPaused, isSupported, isProcessing, pendingChunks, transcriptionError, retryingChunks, failedChunks, retryFailedChunks, start, stop, pause, resume, streamRef };
}
